import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarPlus, Plus, Trash2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { docenteLabel } from "@/lib/format";

const GRADOS = [1, 2, 3, 4, 5, 6];
const ACTION_LABELS = {
  asignar: "Asignar grado y grupo",
  sin_grupo: "Sin grupo (mantiene alta)",
  comision: "Otra comisión",
  baja: "Dar de baja",
};

export default function IniciarCiclo() {
  const { user } = useAuth();
  const [plantelId, setPlantelId] = useState("");
  const [plantel, setPlantel] = useState(null);
  const [cicloActual, setCicloActual] = useState("2025-2026");
  const [nuevoCiclo, setNuevoCiclo] = useState("2026-2027");
  const [docentes, setDocentes] = useState([]);
  const [gruposActuales, setGruposActuales] = useState([]);
  const [asignaciones, setAsignaciones] = useState({}); // id -> {action, grado, letra, comision_desc}
  const [nuevos, setNuevos] = useState([]); // [{email, nombre, apellido_paterno, apellido_materno, grado, letra}]
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null); // grupo egresado
  const [purgeStep, setPurgeStep] = useState(1); // 1 primer confirm, 2 segundo

  useEffect(() => {
    const pid = (user?.plantel_ids || [])[0];
    if (pid) setPlantelId(pid);
  }, [user]);

  useEffect(() => {
    if (!plantelId) return;
    (async () => {
      try {
        const [p, u, g] = await Promise.all([
          api.get(`/planteles/${plantelId}`),
          api.get("/usuarios", { params: { role: "docente" } }),
          api.get("/grupos", { params: { plantel_id: plantelId } }),
        ]);
        setPlantel(p.data);
        const mios = (u.data || []).filter((d) => (d.plantel_ids || []).includes(plantelId));
        setDocentes(mios);
        setGruposActuales(g.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [plantelId]);

  const grupoActualPorDocente = useMemo(() => {
    const map = {};
    (gruposActuales || [])
      .filter((g) => g.ciclo === cicloActual && !g.es_egresado)
      .forEach((g) => { if (g.docente_id) map[g.docente_id] = g; });
    return map;
  }, [gruposActuales, cicloActual]);

  const egresados = useMemo(
    () => (gruposActuales || []).filter((g) => g.es_egresado),
    [gruposActuales]
  );

  const setAsig = (docId, patch) => {
    setAsignaciones((prev) => ({ ...prev, [docId]: { ...(prev[docId] || {}), ...patch } }));
  };

  const submit = async () => {
    setRunning(true);
    try {
      const asigList = docentes.map((d) => {
        const a = asignaciones[d.id] || {};
        // Sugerencia default: asignar y proponer subir de grado
        const suggestedGrado = grupoActualPorDocente[d.id]?.grado
          ? Math.min(6, grupoActualPorDocente[d.id].grado + 1)
          : 1;
        const suggestedLetra = grupoActualPorDocente[d.id]?.letra || "A";
        return {
          docente_id: d.id,
          action: a.action || "asignar",
          grado: (a.action || "asignar") === "asignar" ? parseInt(a.grado || suggestedGrado, 10) : null,
          letra: (a.action || "asignar") === "asignar" ? (a.letra || suggestedLetra).toUpperCase() : null,
          comision_desc: a.action === "comision" ? (a.comision_desc || "") : null,
        };
      });
      const nuevosClean = nuevos
        .filter((n) => n.email && n.nombre && n.grado && n.letra)
        .map((n) => ({
          email: n.email.trim().toLowerCase(),
          nombre: n.nombre.trim(),
          apellido_paterno: (n.apellido_paterno || "").trim(),
          apellido_materno: (n.apellido_materno || "").trim(),
          grado: parseInt(n.grado, 10),
          letra: n.letra.toUpperCase(),
        }));
      const { data } = await api.post(`/planteles/${plantelId}/iniciar-ciclo`, {
        nuevo_ciclo: nuevoCiclo,
        ciclo_actual: cicloActual,
        asignaciones: asigList,
        nuevos_docentes: nuevosClean,
      });
      toast.success(
        `Ciclo ${nuevoCiclo} iniciado — ${data.grupos_creados} grupos · ${data.alumnos_copiados} alumnos · ${data.egresados_alumnos} egresados · ${data.docentes_creados} docentes creados`,
      );
      setConfirmOpen(false);
      setNuevos([]);
      // Refresh grupos
      const g = await api.get("/grupos", { params: { plantel_id: plantelId } });
      setGruposActuales(g.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const confirmPurge = async () => {
    if (!purgeTarget) return;
    try {
      await api.delete(`/grupos/${purgeTarget.id}/purge-egresado`);
      toast.success(`Grupo egresado eliminado definitivamente`);
      setPurgeTarget(null); setPurgeStep(1);
      const g = await api.get("/grupos", { params: { plantel_id: plantelId } });
      setGruposActuales(g.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Administración</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Iniciar nuevo ciclo escolar
          </h1>
          <p className="text-sm text-[#545454] mt-1">
            Reasigna a cada docente en el nuevo ciclo. Los alumnos suben de grado automáticamente conservando la misma letra.
          </p>
        </div>

        <Card className="border-[#E5E7EB] mb-4">
          <CardContent className="p-5 grid md:grid-cols-3 gap-4">
            {(() => {
              const parseStart = (c) => parseInt((c || "").split("-")[0], 10) || new Date().getFullYear();
              const y = parseStart(cicloActual);
              const anteriorCiclo = `${y - 1}-${y}`;
              const siguienteCiclo = `${y + 1}-${y + 2}`;
              const options = [anteriorCiclo, cicloActual, siguienteCiclo];
              // Asegura que nuevoCiclo por defecto sea el siguiente al actual.
              if (nuevoCiclo !== siguienteCiclo && nuevoCiclo !== cicloActual && nuevoCiclo !== anteriorCiclo) {
                // Si el estado tenía un valor antiguo, dejamos que el usuario lo re-seleccione.
              }
              return (
                <>
                  <div>
                    <Label className="text-xs">Ciclo actual</Label>
                    <Select value={cicloActual} onValueChange={setCicloActual}>
                      <SelectTrigger data-testid="ciclo-actual"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Nuevo ciclo *</Label>
                    <Select value={nuevoCiclo} onValueChange={setNuevoCiclo}>
                      <SelectTrigger data-testid="nuevo-ciclo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <div className="text-xs text-[#6B7280]">
                      Plantel: <strong className="text-[#10312B]">{plantel?.nombre || "—"}</strong>
                      <span className="ml-2 font-mono text-[10px]">{plantel?.cct}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Docentes existentes */}
        <Card className="border-[#E5E7EB] mb-4">
          <CardContent className="p-0">
            <div className="p-4 flex items-center justify-between border-b border-[#E5E7EB]">
              <div>
                <div className="text-sm font-semibold text-[#10312B]">Docentes actuales del plantel</div>
                <div className="text-xs text-[#6B7280]">{docentes.length} docente(s). Configura la asignación de cada uno.</div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Docente</TableHead>
                  <TableHead>Ciclo actual</TableHead>
                  <TableHead>Acción</TableHead>
                  <TableHead>Nuevo grado</TableHead>
                  <TableHead>Nueva letra</TableHead>
                  <TableHead>Comisión</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docentes.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-[#9CA3AF] py-6">Sin docentes en el plantel.</TableCell></TableRow>
                ) : docentes.map((d) => {
                  const a = asignaciones[d.id] || {};
                  const cur = grupoActualPorDocente[d.id];
                  const action = a.action || "asignar";
                  const sugGrado = cur?.grado ? Math.min(6, cur.grado + 1) : 1;
                  return (
                    <TableRow key={d.id} data-testid={`asig-row-${d.id}`}>
                      <TableCell className="font-medium">{docenteLabel(d)}</TableCell>
                      <TableCell className="text-xs">{cur ? `${cur.grado}°${cur.letra}` : <span className="text-[#9CA3AF]">Sin grupo</span>}</TableCell>
                      <TableCell>
                        <Select value={action} onValueChange={(v) => setAsig(d.id, { action: v })}>
                          <SelectTrigger data-testid={`asig-action-${d.id}`} className="w-[180px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(ACTION_LABELS).map(([k, l]) => (
                              <SelectItem key={k} value={k}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {action === "asignar" && (
                          <Select value={String(a.grado || sugGrado)} onValueChange={(v) => setAsig(d.id, { grado: v })}>
                            <SelectTrigger className="w-[90px]" data-testid={`asig-grado-${d.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {GRADOS.map((g) => <SelectItem key={g} value={String(g)}>{g}°</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        {action === "asignar" && (
                          <Input
                            className="w-[80px] uppercase font-mono"
                            value={a.letra ?? (cur?.letra || "A")}
                            onChange={(e) => setAsig(d.id, { letra: e.target.value.toUpperCase() })}
                            maxLength={3}
                            data-testid={`asig-letra-${d.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {action === "comision" && (
                          <Input
                            placeholder="Descripción de la comisión"
                            value={a.comision_desc || ""}
                            onChange={(e) => setAsig(d.id, { comision_desc: e.target.value })}
                            data-testid={`asig-comision-${d.id}`}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Nuevos docentes */}
        <Card className="border-[#E5E7EB] mb-4">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-[#10312B]">Agregar docentes nuevos (opcional)</div>
                <div className="text-xs text-[#6B7280]">Se crean sobre la marcha con contraseña provisional <span className="font-mono">test12345</span>.</div>
              </div>
              <Button
                type="button" variant="outline" data-testid="btn-add-nuevo-docente"
                onClick={() => setNuevos((n) => [...n, { email: "", nombre: "", apellido_paterno: "", apellido_materno: "", grado: "1", letra: "A" }])}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Agregar docente
              </Button>
            </div>
            {nuevos.length > 0 && (
              <div className="space-y-2">
                {nuevos.map((n, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-7 gap-2 p-3 bg-[#F9FAFB] rounded border border-[#F3F4F6]" data-testid={`nuevo-doc-row-${i}`}>
                    <Input placeholder="Nombre(s) *" value={n.nombre} onChange={(e) => setNuevos((nn) => nn.map((x, k) => k === i ? { ...x, nombre: e.target.value } : x))} />
                    <Input placeholder="Apellido paterno" value={n.apellido_paterno} onChange={(e) => setNuevos((nn) => nn.map((x, k) => k === i ? { ...x, apellido_paterno: e.target.value } : x))} />
                    <Input placeholder="Apellido materno" value={n.apellido_materno} onChange={(e) => setNuevos((nn) => nn.map((x, k) => k === i ? { ...x, apellido_materno: e.target.value } : x))} />
                    <Input placeholder="email *" type="email" value={n.email} onChange={(e) => setNuevos((nn) => nn.map((x, k) => k === i ? { ...x, email: e.target.value } : x))} />
                    <Select value={String(n.grado)} onValueChange={(v) => setNuevos((nn) => nn.map((x, k) => k === i ? { ...x, grado: v } : x))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{GRADOS.map((g) => <SelectItem key={g} value={String(g)}>{g}°</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="uppercase" placeholder="Letra" value={n.letra} onChange={(e) => setNuevos((nn) => nn.map((x, k) => k === i ? { ...x, letra: e.target.value.toUpperCase() } : x))} maxLength={3} />
                    <Button variant="ghost" size="sm" onClick={() => setNuevos((nn) => nn.filter((_, k) => k !== i))}>
                      <Trash2 className="w-3.5 h-3.5 text-[#9F2241]" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Egresados existentes */}
        {egresados.length > 0 && (
          <Card className="border-[#E5E7EB] mb-4 bg-[#FEF3F2]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-[#9F2241]" />
                <div className="text-sm font-semibold text-[#10312B]">Grupos egresados de ciclos previos</div>
              </div>
              <div className="text-xs text-[#545454] mb-3">
                Se conservan como <strong>Egresado {"{grado}°{letra}"}</strong> para llenar el 911 del nuevo ciclo. Cuando ya no los necesites, puedes eliminarlos definitivamente.
              </div>
              <div className="space-y-1">
                {egresados.map((g) => (
                  <div key={g.id} className="flex items-center justify-between p-2 bg-white rounded border border-[#F3F4F6]" data-testid={`egresado-row-${g.id}`}>
                    <div className="text-sm">
                      <span className="font-semibold text-[#9F2241]">EGRESADO {g.grado}°{g.letra}</span>
                      <span className="ml-2 text-xs text-[#6B7280]">ciclo {g.ciclo}</span>
                    </div>
                    <Button
                      size="sm" variant="ghost" className="text-[#9F2241] hover:bg-[#9F2241]/10"
                      onClick={() => { setPurgeTarget(g); setPurgeStep(1); }}
                      data-testid={`btn-purge-${g.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar definitivamente
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button
            className="bg-[#235B4E] hover:bg-[#1D4A40]" data-testid="btn-iniciar-ciclo"
            disabled={running || !plantelId}
            onClick={() => setConfirmOpen(true)}
          >
            <CalendarPlus className="w-4 h-4 mr-1" /> Iniciar ciclo {nuevoCiclo}
          </Button>
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar inicio de ciclo</AlertDialogTitle>
              <AlertDialogDescription>
                Vas a crear el ciclo <strong>{nuevoCiclo}</strong> a partir de <strong>{cicloActual}</strong>. Los alumnos activos subirán de grado (misma letra). Los 6° quedarán marcados como egresados. Los docentes se reasignan según la tabla. Esta acción no puede deshacerse desde aquí.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={submit} className="bg-[#235B4E] hover:bg-[#1D4A40]" data-testid="btn-confirm-iniciar">
                {running ? "Procesando…" : "Sí, iniciar ciclo"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!purgeTarget} onOpenChange={(o) => { if (!o) { setPurgeTarget(null); setPurgeStep(1); } }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#9F2241]" />
                {purgeStep === 1 ? "Eliminar grupo egresado" : "Confirmar eliminación definitiva"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {purgeStep === 1 ? (
                  <>Vas a eliminar <strong>EGRESADO {purgeTarget?.grado}°{purgeTarget?.letra}</strong> del ciclo {purgeTarget?.ciclo} junto con TODOS sus alumnos. Esta acción no puede deshacerse.</>
                ) : (
                  <>¿Estás completamente seguro? Esta es tu última oportunidad para cancelar.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setPurgeTarget(null); setPurgeStep(1); }}>Cancelar</AlertDialogCancel>
              {purgeStep === 1 ? (
                <AlertDialogAction onClick={(e) => { e.preventDefault(); setPurgeStep(2); }} className="bg-[#9F2241] hover:bg-[#7f1a34]" data-testid="btn-purge-step1">
                  Continuar
                </AlertDialogAction>
              ) : (
                <AlertDialogAction onClick={confirmPurge} className="bg-[#9F2241] hover:bg-[#7f1a34]" data-testid="btn-purge-step2">
                  Sí, eliminar definitivamente
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
